import { BucketPerformancePoint } from "helpers/types";
import { ChartSpec } from "./chartSeriesSelection";

/** One line of the overlaid chart, aligned to the shared date axis. */
export interface OverlayLine {
  key: string;
  /** What the legend and the tooltip call it — "Bucket 2: TSLA". */
  title: string;
  color: string;
  /** SVG `stroke-dasharray`; "" for a solid line. */
  dash: string;
  /**
   * The compounded total at each date of the shared axis, null on a date this
   * series has no priced day for. The line is drawn ACROSS such a null rather
   * than broken at it (PerformanceLineChart's `spanGaps`): the axis carries
   * only days something was measured on (see the axis rule below), so a null
   * left on it is one series' own missing day between two days it does have,
   * and joining them reads as the gap it is where a broken line reads as the
   * end of the series.
   */
  totals: (number | null)[];
  /**
   * This series' own point at each date of the shared axis. The tooltip needs
   * the point and not just the plotted total, because the day's own move is
   * READ from the series — differencing the compounded totals would report
   * the change in the index instead of the move that produced it.
   */
  points: (BucketPerformancePoint | null)[];
}

/** A series the reader selected and the backend refused, with its reason. */
export interface UnavailableLine {
  key: string;
  title: string;
  reason: string;
}

export interface OverlayChartData {
  /** The shared X axis: every date something was MEASURED on (axisDates). */
  dates: string[];
  lines: OverlayLine[];
  unavailable: UnavailableLine[];
  /**
   * Every drawn point of every line, flat. The Y axis is fitted across the
   * whole overlay rather than per line — one set of axes can only carry one
   * range, and fitting to a single series would push the others off the plot.
   */
  points: BucketPerformancePoint[];
}

/** One drawn series' points, keyed by date for the axis rule and the padding. */
interface DrawableSeries {
  spec: ChartSpec;
  points: BucketPerformancePoint[];
  byDate: Map<string, BucketPerformancePoint>;
}

/**
 * The shared X axis: the union of the drawn series' dates, MINUS the days on
 * which nothing was measured.
 *
 * The two kinds of bucket hold different calendars. A pair bucket's line and a
 * member's carry TRADING days — one point per day that security was priced. But
 * Bucket 3's carries every CALENDAR day after its seed: a day Orion could not
 * price is carried flat at the previous day's value and still gets a point, so
 * the account's line has no holes in it (bucket_performance_calculations.md §6).
 *
 * A plain union therefore puts every weekend and market holiday on the shared
 * axis on the strength of Bucket 3 alone, and Buckets 1 and 2 have nothing to
 * show there. That is the defect this rule exists to remove: the reader saw a
 * chart in which the pair-bucket lines dashed 5-days-on-2-days-off from the day
 * Bucket 3's line began, and a tooltip on Sunday 05/24/2026 that reported
 * Bucket 3 and no other bucket at all.
 *
 * So a date is kept when EITHER
 *  - some drawn line MOVED on it — its point reports a `daily` that is not 0;
 *    a null `daily` is a baseline point and counts, since a series' opening day
 *    is a real priced day and the index is 100% *of* it — OR
 *  - every drawn line has a point on it, so no line is padded there at all.
 *
 * and dropped only when both fail: a day some line is missing from, on which
 * every line that does have a point reports no move. A flat carry is exactly
 * that by construction — its value IS the previous day's, so its `daily` is 0
 * to the last place — while a trading day survives on the first clause as soon
 * as anything moved, and on the second even if nothing did. Reading the carry
 * off the figure rather than off which bucket the line belongs to is what keeps
 * a genuinely priced Bucket 3 day on the axis when the pair buckets have ended.
 *
 * Bucket 3 drawn ALONE keeps its whole calendar: no line is missing anywhere,
 * so the second clause holds on every date and the weekends read as the flat
 * stretches they were, which is what that line on its own has always shown.
 */
const axisDates = (drawable: DrawableSeries[], allDates: Set<string>): string[] =>
  // Sorting ISO date strings lexicographically IS sorting them chronologically.
  Array.from(allDates)
    .sort()
    .filter((date) => {
      let padded = false;
      let moved = false;
      for (const { byDate } of drawable) {
        const point = byDate.get(date);
        if (!point) padded = true;
        else if (point.daily !== 0) moved = true;
      }
      return moved || !padded;
    });

/**
 * The selected series, aligned onto ONE set of axes.
 *
 * Each series carries its own days: a series opens on its own baseline, which
 * is by construction a priced day strictly before the start date, and ends on
 * the last day its own inputs were priced. So overlaying them means a union of
 * dates with each line padded to it, not an assumption that they share a
 * calendar — narrowed to the days something was measured on by axisDates above.
 *
 * A series the backend refused is separated out rather than dropped: one
 * refused security no longer costs a chart of its own, so its reason is
 * reported beside the lines that did come back. A payload carrying neither
 * points nor a reason contributes to neither list — there is nothing to draw
 * and nothing to say.
 */
export const buildOverlayChartData = (
  specs: ChartSpec[],
): OverlayChartData => {
  const drawable: DrawableSeries[] = [];
  const unavailable: UnavailableLine[] = [];
  const allDates = new Set<string>();

  for (const spec of specs) {
    const points = spec.series.points;
    if (points) {
      // Keyed by date rather than walked in step with the axis: a series' own
      // points are already in date order, but nothing promises they hold EVERY
      // date of the axis, and a lookup does not care.
      const byDate = new Map(points.map((point) => [point.date, point]));
      drawable.push({ spec, points, byDate });
      for (const point of points) allDates.add(point.date);
      continue;
    }
    if (spec.series.unavailable) {
      unavailable.push({
        key: spec.key,
        title: spec.title,
        reason: spec.series.unavailable,
      });
    }
  }

  const dates = axisDates(drawable, allDates);
  const points: BucketPerformancePoint[] = [];

  const lines = drawable.map(({ spec, points: seriesPoints, byDate }) => {
    const aligned = dates.map((date) => byDate.get(date) ?? null);
    // Every point the SERIES carries, not only the ones the axis kept. A day
    // axisDates dropped moved nothing, so its total repeats a kept day's and
    // cannot widen the fitted range — and gathering from the series is what
    // keeps this a walk over the payload rather than over the axis.
    //
    // Pushed one at a time, never spread: the series is as long as the period
    // the reader picked, and argument spread is bounded by the engine's call
    // stack — a long enough one threw RangeError during render and blanked
    // the whole report.
    for (const point of seriesPoints) points.push(point);
    return {
      key: spec.key,
      title: spec.title,
      color: spec.color,
      dash: spec.dash,
      totals: aligned.map((point) => point?.total ?? null),
      points: aligned,
    };
  });

  return { dates, lines, unavailable, points };
};
